import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Props } from "./interfaces";
import { Table, Typography, TableHead, TableRow, TableBody, Grid } from "@mui/material";
import { Container, SortContainer, StyledTableCell, TableContainer, TitleContainer } from "./styles";
import AbsencesRow from "./AbsencesRow/AbsencesRow";
import SearchTableMessage from "../../../../reusable-components/SearchTableMessage/SearchTableMessage";
import CustomTablePagination from "../../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { ListSortDirection } from "../../../../models/api/sort";

type ColumnType = {
  title: string;
  field: string;
  width: number;
};

const AbsencesTable = ({
  searchOptions,
  totalItems,
  items,
  loadData,
  loading,
  error,
  page,
  handleSetPage,
  tableHeight,
  minDate,
  maxDate,
  allowedOnlyApproveReject = false,
  allEmployeesSelected = false,
  sort,
  handleSetSort,
  counters,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_leaves";
  const translationFields = `${tRP}.fields`;

  const getWidth = (fieldTitle: string): number => {
    const field = columns.find((obj) => obj.field === fieldTitle);

    if (field) return field.width;

    return 0;
  };

  const columns: ColumnType[] = [
    {
      title: "",
      field: "status",
      width: 10,
    },
    {
      title: t(`${translationFields}.employee`),
      field: "employee",
      width: 10,
    },
    {
      title: t(`${translationFields}.absence_type`),
      field: "absenceType",
      width: 10,
    },
    {
      title: t(`${translationFields}.from_date`),
      field: "fromDate",
      width: 10,
    },
    {
      title: t(`${translationFields}.to_date`),
      field: "toDate",
      width: 10,
    },
    {
      title: t(`common_words.document`),
      field: "documentation",
      width: 10,
    },
    {
      title: t(`${translationFields}.is_approved_rejected`),
      field: "isApproved, isRejected",
      width: 25,
    },
    {
      title: t(`${translationFields}.accepted_rejected_by_client`),
      field: "acceptedByClient, rejectedByClient",
      width: 25,
    },
  ];

  const handleSort = useCallback(
    (column: ColumnType) => {
      const fieldName = column.field === "employee" ? "name" : column.field;
      if (sort[0].name === fieldName) {
        handleSetSort(
          fieldName,
          ListSortDirection.Ascending === sort[0].direction ? ListSortDirection.Descending : ListSortDirection.Ascending
        );
      } else {
        handleSetSort(fieldName, ListSortDirection.Ascending);
      }
    },
    [sort]
  );

  const checkForRequiredSort = useCallback((column: ColumnType) => {
    return (
      column.field === "employee" ||
      column.field === "absenceType" ||
      column.field === "fromDate" ||
      column.field === "toDate"
    );
  }, []);

  return (
    <>
      <Container
        style={{
          height: `${tableHeight}px`,
          marginTop: "10px",
        }}
      >
        <TableContainer>
          {totalItems > 0 ? (
            <Table
              size={"small"}
              stickyHeader={true}
            >
              <TableHead>
                <TableRow>
                  {columns.map((column, idx) => (
                    <StyledTableCell
                      key={idx}
                      align="center"
                      width={column.width}
                    >
                      <TitleContainer>
                        <Typography>{column.title}</Typography>
                        {checkForRequiredSort(column) && (
                          <SortContainer
                            title={`Sort by ${column.title.toLowerCase()}`}
                            onClick={() => handleSort(column)}
                          >
                            <FontAwesomeIcon
                              icon={
                                (sort[0].name === column.field || sort[0].name === "name") &&
                                sort[0].direction === ListSortDirection.Descending
                                  ? faArrowDown
                                  : faArrowUp
                              }
                            />
                          </SortContainer>
                        )}
                      </TitleContainer>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <AbsencesRow
                      item={item}
                      getWidth={getWidth}
                      handleNotifyParent={loadData}
                      minDate={minDate}
                      maxDate={maxDate}
                      allowedOnlyApproveReject={allowedOnlyApproveReject}
                      allEmployeesSelected={allEmployeesSelected}
                      counters={counters}
                    />
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          ) : (
            <SearchTableMessage
              loading={loading}
              searchOptions={searchOptions}
              error={error}
            />
          )}
        </TableContainer>
      </Container>
      <CustomTablePagination
        totalItems={totalItems}
        pageNumber={page.pageNumber}
        handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
        pageSize={page.pageSize}
        handleChangePageSize={(s) => {
          handleSetPage("pageSize", s);
          handleSetPage("pageNumber", 0);
        }}
      />
    </>
  );
};

export default AbsencesTable;
